import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The "Pathways" part of a Hide and Seek game's statistics: pick a finished
 * round and see the route each player wearing Krall's Accord took through it
 * (HnsPathwaysMap). Rounds still to be played, or in play, aren't offered: a
 * round's paths show only once it has ended.
 *
 * Players' clients ask for the paths by room code, admin-web by game id (so
 * archived games work too); `useRoundPaths` fetches them and follows late
 * uploads. HnsPathwaysBrowser is the same view for paths fetched any other way
 * (the public results page fetches them over HTTP).
 */
import { useState } from 'react';
import { useRoundPaths } from '@swtorbit/shared-react-hooks';
import { HnsPathwaysMap } from './HnsPathwaysMap';
/** A game's finished rounds, latest first. */
export function endedHnsRounds(game) {
    return game.rounds
        .map((round, index) => ({ round, index }))
        .filter(({ round }) => round.status === 'completed' || round.status === 'abandoned')
        .reverse();
}
/** The round to show: the one picked, if it has ended, else the latest that has. */
export function pickedHnsRoundIndex(game, picked) {
    const ended = endedHnsRounds(game);
    return ended.some((r) => r.index === picked) ? picked : ended[0]?.index ?? null;
}
export function HnsPathwaysBrowser({ game, roundIndex, onPickRound, paths, loading, error, viewerId = null, resolveImageUrl }) {
    const ended = endedHnsRounds(game);
    if (ended.length === 0) {
        return _jsx("p", { className: "fs-sm text-slate m-0", children: "Each player's route shows here once a round has ended." });
    }
    const round = roundIndex !== null ? game.rounds[roundIndex] : undefined;
    return (_jsxs("div", { className: "d-flex flex-column gap-xs", children: [_jsxs("div", { className: "d-flex flex-wrap align-items-center gap-xs", children: [_jsx("select", { className: "select select--sm", "aria-label": "Round", value: roundIndex ?? '', onChange: (event) => onPickRound(Number(event.target.value)), children: ended.map(({ round: r, index }) => (_jsxs("option", { value: index, children: ["Round ", index + 1, r.status === 'abandoned' ? ' (abandoned)' : '', r.hnsLocation ? ` · ${r.hnsLocation.areaName}` : ''] }, r.id))) }), _jsx("span", { className: "fs-xs text-slate", children: "Routes of the players wearing Krall's Accord." })] }), !round?.hnsLocation ? (_jsx("p", { className: "fs-sm text-slate m-0", children: "No map for this round." })) : error ? (_jsx("p", { className: "fs-sm text-red m-0", children: error })) : loading || !paths ? (_jsx("p", { className: "fs-sm text-slate m-0", children: "Loading paths\u2026" })) : (_jsx(HnsPathwaysMap, { round: round, location: round.hnsLocation, players: game.players, paths: paths, viewerId: viewerId, resolveImageUrl: resolveImageUrl }))] }));
}
export function HnsPathwaysSection({ game, gameId, viewerId = null, resolveImageUrl }) {
    const [picked, setPicked] = useState(null);
    const roundIndex = pickedHnsRoundIndex(game, picked);
    const { paths, loading, error } = useRoundPaths({ roomCode: game.roomCode, gameId, roundIndex });
    return (_jsx(HnsPathwaysBrowser, { game: game, roundIndex: roundIndex, onPickRound: setPicked, paths: paths, loading: loading, error: error, viewerId: viewerId, resolveImageUrl: resolveImageUrl }));
}
